import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { shipmentReturnService } from '../../services/shipmentReturnService';
import { useAuth } from '../../context/AuthContext';
import Alert from '../common/Alert';
import { RotateCcw, Plus, Eye, Pencil } from 'lucide-react';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const ReturnList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({ search: '', return_status: '' });

  useEffect(() => {
    fetchReturns();
  }, [page, filterValues.return_status]);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      const filters = filterValues.return_status ? { return_status: filterValues.return_status } : {};
      const response = await shipmentReturnService.getAllReturns(page, 10, filters);

      const returnsData = response.data.returns || response.data.data || [];
      setReturns(returnsData);

      const pagination = response.data.pagination || {};
      setTotalPages(pagination.pages || 1);
    } catch (error) {
      console.error('Error fetching returns:', error);
      Alert.error(error.response?.data?.error || 'Failed to fetch returns');
      setReturns([]);
    } finally {
      setLoading(false);
    }
  };

  const canEditReturn = () => {
    const allowedRoles = ['System Admin', 'Admin', 'Accountant'];
    return allowedRoles.includes(user?.role);
  };

  const isShipmentClosed = (shipmentData) => shipmentData?.status?.toLowerCase() === 'closed';

  const canShowEditIcon = (returnStatus, shipmentData) =>
    canEditReturn() && !isShipmentClosed(shipmentData);

  const filteredReturns = returns.filter((ret) => {
    const searchLower = filterValues.search.toLowerCase();
    if (!searchLower) return true;
    return (
      ret.return_id?.toLowerCase().includes(searchLower) ||
      ret.shipment_id?.toLowerCase().includes(searchLower) ||
      ret.created_by?.toLowerCase().includes(searchLower)
    );
  });

  const columns = [
    {
      key: 'return_id',
      label: 'Return ID',
      render: (ret) => (
        <button
          onClick={() => navigate(`/returns/view/${ret.return_id}`)}
          className="font-medium text-brand hover:underline"
        >
          {ret.return_id}
        </button>
      ),
    },
    {
      key: 'shipment_id',
      label: 'Shipment ID',
      render: (ret) => (
        <button
          onClick={() => navigate(`/shipments/view/${ret.shipment_id}`)}
          className="font-medium text-brand hover:underline"
        >
          {ret.shipment_id}
        </button>
      ),
    },
    {
      key: 'reason_for_return',
      label: 'Reason',
      render: (ret) => (
        <span className="truncate block max-w-xs" title={ret.reason_for_return}>
          {ret.reason_for_return?.substring(0, 50)}
          {ret.reason_for_return?.length > 50 ? '...' : ''}
        </span>
      ),
    },
    {
      key: 'return_status',
      label: 'Status',
      render: (ret) => <StatusBadge status={ret.return_status} />,
    },
    { key: 'created_by', label: 'Created By', render: (ret) => ret.created_by || 'N/A' },
    {
      key: 'created_at',
      label: 'Date',
      render: (ret) =>
        ret.created_at
          ? new Date(ret.created_at).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : 'N/A',
    },
  ];

  const getActions = (ret) => {
    const actions = [
      {
        label: 'View Details',
        icon: Eye,
        onClick: () => navigate(`/returns/view/${ret.return_id}`),
      },
    ];

    if (canShowEditIcon(ret.return_status, ret.shipmentData) && ret.return_status === 'pending') {
      actions.push({
        label: 'Manage Return',
        icon: Pencil,
        onClick: () => navigate(`/returns/edit/${ret.return_id}`),
      });
    }

    return actions;
  };

  return (
    <PageLayout
      title="Shipment Returns"
      icon={RotateCcw}
      actions={
        <Button icon={Plus} onClick={() => navigate('/returns/add')}>
          Return Request
        </Button>
      }
    >
      <FilterPanel
        filters={[
          { key: 'search', label: 'Search', placeholder: 'Return ID, shipment ID...' },
          {
            key: 'return_status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'pending', label: 'Pending' },
              { value: 'open', label: 'Open' },
              { value: 'close', label: 'Close' },
            ],
          },
        ]}
        values={filterValues}
        onChange={(values) => {
          setFilterValues(values);
          setPage(1);
        }}
        onClear={() => {
          setFilterValues({ search: '', return_status: '' });
          setPage(1);
        }}
      />

      <DataTable
        columns={columns}
        data={filteredReturns}
        loading={loading}
        emptyMessage="No return requests found"
        rowKey="return_id"
        actions={getActions}
      />

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
    </PageLayout>
  );
};

export default ReturnList;
